import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  type Material,
  MeshBasicMaterial,
  type Object3D,
  SkinnedMesh,
} from 'three';

import type { KeyPoseFkJointId } from '../types';
import type { PoseMeshPickEntry } from './poseMeshPickEntry';

interface PoseMeshHighlightOverlay {
  geometry: BufferGeometry;
  jointId: KeyPoseFkJointId;
  materials: PoseMeshHighlightMaterial[];
  mesh: SkinnedMesh;
  parent: Object3D;
}

interface PoseMeshHighlightSourceMaterial extends Material {
  alphaMap?: MeshBasicMaterial['alphaMap'];
  color?: Color;
  map?: MeshBasicMaterial['map'];
}

interface PoseMeshHighlightMaterial extends MeshBasicMaterial {
  userData: MeshBasicMaterial['userData'] & {
    poseMeshHighlightUniforms?: PoseMeshHighlightUniforms;
  };
}

interface PoseMeshHighlightUniforms {
  tintColor: { value: Color };
  tintStrength: { value: number };
}

interface PoseMeshHighlightTriangle {
  materialIndex: number;
  vertexIndices: [number, number, number];
}

export interface PoseMeshHighlightState {
  entries: readonly PoseMeshPickEntry[];
  overlaysByJointId: Map<KeyPoseFkJointId, PoseMeshHighlightOverlay[]>;
  visibleHoverJointIds: Set<KeyPoseFkJointId>;
  visibleSelectedJointIds: Set<KeyPoseFkJointId>;
}

const HOVER_COLOR = 0xffffff;
const HOVER_OPACITY = 0.72;
const HOVER_TINT_STRENGTH = 0.62;
const SELECTED_COLOR = 0xff8025;
const SELECTED_OPACITY = 0.88;
const SELECTED_TINT_STRENGTH = 0.78;
const HIGHLIGHT_RENDER_ORDER_OFFSET = 20;
const HIGHLIGHT_ALPHA_ATTRIBUTE = 'poseHighlightAlpha';
export const MOTION_EDITOR_BONE_PICKER_HIGHLIGHT_OVERLAY_USER_DATA_KEY =
  'motionEditorBonePickerHighlightOverlay';

const ignoreRaycast = () => {};

export function isPoseMeshHighlightOverlay(object: Object3D) {
  return (
    object.userData[
      MOTION_EDITOR_BONE_PICKER_HIGHLIGHT_OVERLAY_USER_DATA_KEY
    ] === true
  );
}

export function removePoseMeshHighlightOverlays(scene: Object3D) {
  const overlays: Object3D[] = [];
  scene.traverse((object) => {
    if (isPoseMeshHighlightOverlay(object)) overlays.push(object);
  });

  for (const overlay of overlays) {
    overlay.parent?.remove(overlay);
  }
}

export function createPoseMeshHighlightState(
  entries: readonly PoseMeshPickEntry[],
): PoseMeshHighlightState {
  return {
    entries,
    overlaysByJointId: new Map(),
    visibleHoverJointIds: new Set(),
    visibleSelectedJointIds: new Set(),
  };
}

export function applyPoseMeshHighlight(
  state: PoseMeshHighlightState | null,
  hoveredJointIds: readonly KeyPoseFkJointId[],
  selectedJointIds: readonly KeyPoseFkJointId[],
) {
  if (!state) return;

  const visibleSelectedJointIds = new Set(selectedJointIds);
  const visibleHoverJointIds = new Set(hoveredJointIds);
  for (const selectedJointId of visibleSelectedJointIds) {
    visibleHoverJointIds.delete(selectedJointId);
  }

  if (
    areJointIdSetsEqual(state.visibleHoverJointIds, visibleHoverJointIds) &&
    areJointIdSetsEqual(state.visibleSelectedJointIds, visibleSelectedJointIds)
  ) {
    return;
  }

  const affectedJointIds = new Set<KeyPoseFkJointId>();
  addJointIds(affectedJointIds, state.visibleHoverJointIds);
  addJointIds(affectedJointIds, state.visibleSelectedJointIds);
  addJointIds(affectedJointIds, visibleHoverJointIds);
  addJointIds(affectedJointIds, visibleSelectedJointIds);

  state.visibleHoverJointIds = visibleHoverJointIds;
  state.visibleSelectedJointIds = visibleSelectedJointIds;

  for (const jointId of affectedJointIds) {
    applyPoseMeshJointOverlayState(state, jointId);
  }
}

export function restorePoseMeshHighlightState(
  state: PoseMeshHighlightState | null,
) {
  if (!state) return;

  disposePoseMeshHighlightOverlays(state.overlaysByJointId);
  state.visibleHoverJointIds.clear();
  state.visibleSelectedJointIds.clear();
}

type PoseMeshHighlightKind = 'hover' | 'selected';

function addJointIds(
  target: Set<KeyPoseFkJointId>,
  source: ReadonlySet<KeyPoseFkJointId>,
) {
  for (const jointId of source) {
    target.add(jointId);
  }
}

function areJointIdSetsEqual(
  left: ReadonlySet<KeyPoseFkJointId>,
  right: ReadonlySet<KeyPoseFkJointId>,
) {
  if (left.size !== right.size) return false;

  for (const jointId of left) {
    if (!right.has(jointId)) return false;
  }

  return true;
}

function applyPoseMeshJointOverlayState(
  state: PoseMeshHighlightState,
  jointId: KeyPoseFkJointId,
) {
  const kind = getPoseMeshHighlightKind(state, jointId);
  const overlays = kind
    ? getPoseMeshHighlightOverlays(state, jointId)
    : (state.overlaysByJointId.get(jointId) ?? []);

  for (const overlay of overlays) {
    overlay.mesh.visible = Boolean(kind);
    if (kind) {
      applyPoseMeshOverlayMaterials(overlay.materials, kind);
    }
  }
}

function getPoseMeshHighlightKind(
  state: PoseMeshHighlightState,
  jointId: KeyPoseFkJointId,
): PoseMeshHighlightKind | null {
  if (state.visibleSelectedJointIds.has(jointId)) return 'selected';
  if (state.visibleHoverJointIds.has(jointId)) return 'hover';
  return null;
}

function getPoseMeshHighlightOverlays(
  state: PoseMeshHighlightState,
  jointId: KeyPoseFkJointId,
) {
  const cachedOverlays = state.overlaysByJointId.get(jointId);
  if (cachedOverlays) return cachedOverlays;

  const overlays: PoseMeshHighlightOverlay[] = [];
  for (const entry of state.entries) {
    const vertices = entry.vertexGroups.get(jointId);
    if (!vertices?.length) continue;

    const overlay = createPoseMeshHighlightOverlay(entry, jointId, vertices);
    if (overlay) overlays.push(overlay);
  }

  state.overlaysByJointId.set(jointId, overlays);
  return overlays;
}

function disposePoseMeshHighlightOverlays(
  overlaysByJointId: Map<KeyPoseFkJointId, PoseMeshHighlightOverlay[]>,
) {
  for (const overlays of overlaysByJointId.values()) {
    for (const overlay of overlays) {
      overlay.parent.remove(overlay.mesh);
      overlay.geometry.dispose();
      for (const material of overlay.materials) {
        material.dispose();
      }
    }
  }

  overlaysByJointId.clear();
}

function createPoseMeshHighlightOverlay(
  entry: PoseMeshPickEntry,
  jointId: KeyPoseFkJointId,
  vertices: readonly number[],
): PoseMeshHighlightOverlay | null {
  const parent = entry.mesh.parent;
  if (!parent || entry.mesh.visible === false) return null;

  const sourceMaterials = getSourceMaterials(entry.mesh.material);
  const geometry = createJointOverlayGeometry(
    entry.mesh.geometry,
    vertices,
    sourceMaterials.length,
  );
  if (!geometry) return null;

  const materials = createHighlightMaterials(sourceMaterials);
  const overlayMesh = new SkinnedMesh(
    geometry,
    materials.length === 1 ? materials[0] : materials,
  );
  syncPoseMeshHighlightMorphTargets(entry.mesh, overlayMesh);
  overlayMesh.name = `${entry.mesh.name || 'mesh'}-${jointId}-highlight`;
  overlayMesh.userData[
    MOTION_EDITOR_BONE_PICKER_HIGHLIGHT_OVERLAY_USER_DATA_KEY
  ] = true;
  overlayMesh.visible = false;
  overlayMesh.castShadow = false;
  overlayMesh.receiveShadow = false;
  overlayMesh.raycast = ignoreRaycast;
  overlayMesh.frustumCulled = entry.mesh.frustumCulled;
  overlayMesh.renderOrder =
    (entry.mesh.renderOrder ?? 0) + HIGHLIGHT_RENDER_ORDER_OFFSET;
  overlayMesh.bindMode = entry.mesh.bindMode;
  overlayMesh.position.copy(entry.mesh.position);
  overlayMesh.quaternion.copy(entry.mesh.quaternion);
  overlayMesh.scale.copy(entry.mesh.scale);
  overlayMesh.matrix.copy(entry.mesh.matrix);
  overlayMesh.matrixAutoUpdate = entry.mesh.matrixAutoUpdate;
  overlayMesh.matrixWorldAutoUpdate = entry.mesh.matrixWorldAutoUpdate;
  overlayMesh.bind(entry.mesh.skeleton, entry.mesh.bindMatrix);

  parent.add(overlayMesh);
  overlayMesh.updateMatrixWorld(true);

  return {
    geometry,
    jointId,
    materials,
    mesh: overlayMesh,
    parent,
  };
}

function getSourceMaterials(sourceMaterial: SkinnedMesh['material']) {
  return Array.isArray(sourceMaterial) ? sourceMaterial : [sourceMaterial];
}

function createHighlightMaterials(sourceMaterials: readonly Material[]) {
  return sourceMaterials.map((sourceMaterial) =>
    createHighlightMaterial(sourceMaterial),
  );
}

function createHighlightMaterial(sourceMaterial: Material) {
  const texturedSourceMaterial =
    sourceMaterial as PoseMeshHighlightSourceMaterial;
  const tintUniforms: PoseMeshHighlightUniforms = {
    tintColor: { value: new Color(HOVER_COLOR) },
    tintStrength: { value: HOVER_TINT_STRENGTH },
  };
  const material = new MeshBasicMaterial({
    alphaMap: texturedSourceMaterial.alphaMap ?? null,
    alphaTest:
      typeof texturedSourceMaterial.alphaTest === 'number'
        ? texturedSourceMaterial.alphaTest
        : 0,
    color:
      texturedSourceMaterial.color instanceof Color
        ? texturedSourceMaterial.color
        : 0xffffff,
    depthTest: true,
    depthWrite: false,
    map: texturedSourceMaterial.map ?? null,
    opacity: HOVER_OPACITY,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    side: DoubleSide,
    transparent: true,
    vertexColors: texturedSourceMaterial.vertexColors === true,
  }) as PoseMeshHighlightMaterial;
  material.visible = sourceMaterial.visible !== false;
  material.userData.poseMeshHighlightUniforms = tintUniforms;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.poseMeshHighlightTintColor = tintUniforms.tintColor;
    shader.uniforms.poseMeshHighlightTintStrength = tintUniforms.tintStrength;
    shader.vertexShader = shader.vertexShader.replace(
      'void main() {',
      `attribute float ${HIGHLIGHT_ALPHA_ATTRIBUTE};
varying float vPoseHighlightAlpha;

void main() {
  vPoseHighlightAlpha = ${HIGHLIGHT_ALPHA_ATTRIBUTE};`,
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `uniform vec3 poseMeshHighlightTintColor;
uniform float poseMeshHighlightTintStrength;
varying float vPoseHighlightAlpha;

void main() {`,
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#include <map_fragment>
diffuseColor.rgb = mix(
  diffuseColor.rgb,
  poseMeshHighlightTintColor,
  poseMeshHighlightTintStrength
);`,
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <alphamap_fragment>',
      `#include <alphamap_fragment>
diffuseColor.a *= vPoseHighlightAlpha;`,
    );
  };
  material.customProgramCacheKey = () => 'motion-editor-pose-highlight-tint-v2';
  material.needsUpdate = true;

  return material;
}

function applyPoseMeshOverlayMaterials(
  materials: readonly PoseMeshHighlightMaterial[],
  kind: PoseMeshHighlightKind,
) {
  for (const material of materials) {
    const uniforms = material.userData.poseMeshHighlightUniforms;
    if (uniforms) {
      uniforms.tintColor.value.set(
        kind === 'hover' ? HOVER_COLOR : SELECTED_COLOR,
      );
      uniforms.tintStrength.value =
        kind === 'hover' ? HOVER_TINT_STRENGTH : SELECTED_TINT_STRENGTH;
    }
    material.opacity = kind === 'hover' ? HOVER_OPACITY : SELECTED_OPACITY;
  }
}

function createJointOverlayGeometry(
  sourceGeometry: BufferGeometry,
  vertices: readonly number[],
  materialCount: number,
) {
  const position = sourceGeometry.getAttribute('position');
  if (!(position instanceof BufferAttribute)) return null;

  const selectedVertices = new Set(vertices);
  const sourceTriangles = getSelectedTriangles(
    sourceGeometry,
    selectedVertices,
    materialCount,
  );
  if (sourceTriangles.length === 0) return null;
  const sourceVertexIndices = getSelectedTriangleVertexIndices(sourceTriangles);

  const geometry = new BufferGeometry();
  for (const [name, sourceAttribute] of Object.entries(
    sourceGeometry.attributes,
  )) {
    if (!(sourceAttribute instanceof BufferAttribute)) continue;
    const attribute = copyAttributeVertices(
      sourceAttribute,
      sourceVertexIndices,
    );
    geometry.setAttribute(name, attribute);
  }
  copyMorphAttributes(sourceGeometry, geometry, sourceVertexIndices);
  geometry.setAttribute(
    HIGHLIGHT_ALPHA_ATTRIBUTE,
    createHighlightAlphaAttribute(sourceVertexIndices, selectedVertices),
  );

  if (
    !geometry.getAttribute('skinIndex') ||
    !geometry.getAttribute('skinWeight')
  ) {
    geometry.dispose();
    return null;
  }

  applyOverlayGeometryGroups(geometry, sourceTriangles, materialCount);
  geometry.computeBoundingSphere();
  return geometry;
}

function syncPoseMeshHighlightMorphTargets(
  sourceMesh: SkinnedMesh,
  overlayMesh: SkinnedMesh,
) {
  if (sourceMesh.morphTargetDictionary) {
    overlayMesh.morphTargetDictionary = sourceMesh.morphTargetDictionary;
  }

  if (sourceMesh.morphTargetInfluences) {
    overlayMesh.morphTargetInfluences = sourceMesh.morphTargetInfluences;
  }
}

function copyMorphAttributes(
  sourceGeometry: BufferGeometry,
  targetGeometry: BufferGeometry,
  sourceVertexIndices: readonly number[],
) {
  const targetMorphAttributes: BufferGeometry['morphAttributes'] = {};

  for (const [name, sourceMorphAttributes] of Object.entries(
    sourceGeometry.morphAttributes,
  )) {
    const attributes: BufferAttribute[] = [];
    for (const sourceAttribute of sourceMorphAttributes) {
      if (!(sourceAttribute instanceof BufferAttribute)) {
        attributes.length = 0;
        break;
      }
      attributes.push(
        copyAttributeVertices(sourceAttribute, sourceVertexIndices),
      );
    }

    if (attributes.length > 0) {
      targetMorphAttributes[name] = attributes;
    }
  }

  targetGeometry.morphAttributes = targetMorphAttributes;
  targetGeometry.morphTargetsRelative = sourceGeometry.morphTargetsRelative;
}

function createHighlightAlphaAttribute(
  sourceVertexIndices: readonly number[],
  selectedVertices: ReadonlySet<number>,
) {
  const alpha = new Float32Array(sourceVertexIndices.length);
  for (
    let targetVertex = 0;
    targetVertex < sourceVertexIndices.length;
    targetVertex += 1
  ) {
    const sourceVertexIndex = sourceVertexIndices[targetVertex];
    alpha[targetVertex] =
      sourceVertexIndex !== undefined && selectedVertices.has(sourceVertexIndex)
        ? 1
        : 0;
  }

  return new BufferAttribute(alpha, 1);
}

function getSelectedTriangles(
  geometry: BufferGeometry,
  selectedVertices: ReadonlySet<number>,
  materialCount: number,
) {
  const position = geometry.getAttribute('position');
  if (!(position instanceof BufferAttribute)) {
    return [] as PoseMeshHighlightTriangle[];
  }

  const index = geometry.index;
  const triangleCount = index
    ? Math.floor(index.count / 3)
    : Math.floor(position.count / 3);
  const triangles: PoseMeshHighlightTriangle[] = [];

  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    const offset = triangle * 3;
    const ia = index ? index.getX(offset) : offset;
    const ib = index ? index.getX(offset + 1) : offset + 1;
    const ic = index ? index.getX(offset + 2) : offset + 2;

    if (
      selectedVertices.has(ia) ||
      selectedVertices.has(ib) ||
      selectedVertices.has(ic)
    ) {
      triangles.push({
        materialIndex: getTriangleMaterialIndex(
          geometry,
          offset,
          materialCount,
        ),
        vertexIndices: [ia, ib, ic],
      });
    }
  }

  return triangles;
}

function getSelectedTriangleVertexIndices(
  triangles: readonly PoseMeshHighlightTriangle[],
) {
  const vertexIndices: number[] = [];
  for (const triangle of triangles) {
    vertexIndices.push(...triangle.vertexIndices);
  }

  return vertexIndices;
}

function getTriangleMaterialIndex(
  geometry: BufferGeometry,
  triangleOffset: number,
  materialCount: number,
) {
  if (materialCount <= 1) return 0;

  for (const group of geometry.groups) {
    if (
      triangleOffset >= group.start &&
      triangleOffset < group.start + group.count
    ) {
      return normalizeMaterialIndex(group.materialIndex ?? 0, materialCount);
    }
  }

  return 0;
}

function normalizeMaterialIndex(materialIndex: number, materialCount: number) {
  return materialIndex >= 0 && materialIndex < materialCount
    ? materialIndex
    : 0;
}

function applyOverlayGeometryGroups(
  geometry: BufferGeometry,
  triangles: readonly PoseMeshHighlightTriangle[],
  materialCount: number,
) {
  if (materialCount <= 1 || triangles.length === 0) return;

  let groupStart = 0;
  let groupMaterialIndex = triangles[0].materialIndex;

  for (
    let triangleIndex = 1;
    triangleIndex < triangles.length;
    triangleIndex += 1
  ) {
    const triangle = triangles[triangleIndex];
    if (triangle.materialIndex === groupMaterialIndex) continue;

    const groupEnd = triangleIndex * 3;
    geometry.addGroup(groupStart, groupEnd - groupStart, groupMaterialIndex);
    groupStart = groupEnd;
    groupMaterialIndex = triangle.materialIndex;
  }

  const groupEnd = triangles.length * 3;
  geometry.addGroup(groupStart, groupEnd - groupStart, groupMaterialIndex);
}

function copyAttributeVertices(
  sourceAttribute: BufferAttribute,
  sourceVertexIndices: readonly number[],
) {
  const itemSize = sourceAttribute.itemSize;
  const sourceArray = sourceAttribute.array;
  const ArrayCtor = sourceArray.constructor as new (
    length: number,
  ) => BufferAttribute['array'];
  const targetArray = new ArrayCtor(sourceVertexIndices.length * itemSize);

  for (
    let targetVertex = 0;
    targetVertex < sourceVertexIndices.length;
    targetVertex += 1
  ) {
    const sourceVertex = sourceVertexIndices[targetVertex];
    const sourceOffset = sourceVertex * itemSize;
    const targetOffset = targetVertex * itemSize;

    for (let component = 0; component < itemSize; component += 1) {
      targetArray[targetOffset + component] =
        sourceArray[sourceOffset + component] ?? 0;
    }
  }

  return new BufferAttribute(targetArray, itemSize, sourceAttribute.normalized);
}
